import { ChangeDetectionStrategy, Component, HostListener, Input, OnInit } from '@angular/core';

/**
 * Replaces @kolkov/ngx-gallery, which was abandoned and broke on Angular 22: it imports
 * HammerGestureConfig and HAMMER_GESTURE_CONFIG from @angular/platform-browser, both removed in
 * that version.
 *
 * The inputs are unchanged, so the one caller (the Velda article detail modal) is untouched. What
 * it offered and is kept here: a large image, previous/next, a thumbnail strip, and a fullscreen
 * preview. Hammer.js swipe gestures are not carried over -- nothing configured Hammer in this app,
 * so those gestures were already inert.
 */
@Component({
    selector: 'app-image-gallery',
    templateUrl: './image-gallery.component.html',
    styleUrls: ['./image-gallery.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ImageGalleryComponent implements OnInit {
    @Input() small: string[]; //optional
    @Input() medium: string[]; //optional
    @Input() big: string[]; //required

    current = 0;
    previewOpen = false;

    ngOnInit(): void {
        this.big = this.big ?? [];
        if (this.small == undefined || this.small.length <= 0) {
            this.small = this.big;
        }
        if (this.medium == undefined || this.medium.length <= 0) {
            this.medium = this.big;
        }
    }

    /** The thumbnail strip falls back to the big image when no small variant was supplied. */
    thumbnail(index: number): string {
        return this.small[index] ?? this.big[index];
    }

    /** The main image uses the medium variant; the fullscreen preview uses the big one. */
    get displayed(): string {
        return this.previewOpen ? this.big[this.current] : this.medium[this.current] ?? this.big[this.current];
    }

    select(index: number): void {
        this.current = index;
    }

    previous(): void {
        this.current = (this.current - 1 + this.big.length) % this.big.length;
    }

    next(): void {
        this.current = (this.current + 1) % this.big.length;
    }

    openPreview(): void {
        this.previewOpen = true;
    }

    closePreview(): void {
        this.previewOpen = false;
    }

    // Arrow keys and Escape only mean anything while the fullscreen preview is up; outside it they
    // belong to whatever else is on the page.
    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if (!this.previewOpen) {
            return;
        }
        if (event.key === 'Escape') {
            this.closePreview();
        } else if (event.key === 'ArrowLeft') {
            this.previous();
        } else if (event.key === 'ArrowRight') {
            this.next();
        }
    }
}
